import type { GroupRef } from '@ralph/contracts'
import clsx from 'clsx'
import { X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { GroupAvatar, type GroupAvatarSize } from '@/components/groups/GroupAvatar.tsx'
import { useI18n } from '@/i18n/index.ts'

/**
 * A group as every surface names one: its avatar and its name (GRP-102).
 *
 * Never colour alone, never an id. `to` makes it a link to the group's page, which is where a
 * notification or a chip should be able to take somebody who wants to know who is in it.
 */
export const GroupLabel = ({
  group,
  size = 20,
  to,
  className,
  children
}: {
  group: GroupRef
  size?: GroupAvatarSize
  /** Link to the group's page. */
  to?: boolean
  className?: string
  /** Rendered after the name — a count, a mark. */
  children?: React.ReactNode
}) => {
  const body = (
    <>
      <GroupAvatar group={group} size={size} />
      <span className="min-w-0 truncate">{group.name}</span>
      {children}
    </>
  )
  const classes = clsx('inline-flex min-w-0 items-center gap-1.5 align-middle', className)
  return to ? (
    <Link to={`/groups/${group.id}`} className={clsx(classes, 'text-fg hover:text-fg hover:underline')}>
      {body}
    </Link>
  ) : (
    <span className={classes}>{body}</span>
  )
}

/**
 * A chosen group as a removable chip: the shape a picker shows what is chosen in.
 *
 * The remove control carries the group's name in its label ("Remove Finance EMEA"), because a row
 * of identical "×" buttons is a row of controls a screen reader cannot tell apart.
 */
export const GroupChip = ({
  group,
  onRemove,
  removeLabel,
  mark,
  disabled
}: {
  group: GroupRef
  onRemove?: () => void
  removeLabel?: string
  /** A short word after the name: "added", "needs review". */
  mark?: { text: string; tone: 'neutral' | 'primary' | 'alert' }
  disabled?: boolean
}) => {
  // The default label is copy like any other, so it comes from the catalogue rather than from a
  // template here — a caller that omits `removeLabel` still gets the reader's language.
  const t = useI18n().t.groups
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-rule-strong bg-card-solid py-[3px] pl-[3px] pr-2 text-[12.5px] shadow-glass">
      <GroupAvatar group={group} size={20} />
      <span className="min-w-0 truncate font-medium tracking-[-0.012em]">{group.name}</span>
      {mark && (
        <span
          className={clsx(
            'font-mono text-[9px] uppercase tracking-[0.08em]',
            mark.tone === 'primary' && 'text-primary',
            mark.tone === 'alert' && 'text-alert',
            mark.tone === 'neutral' && 'text-fg-subtle'
          )}
        >
          {mark.text}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel ?? t.picker.remove(group.name)}
          disabled={disabled}
          onClick={onRemove}
          className="-mr-1 ml-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full text-fg-subtle transition-colors hover:bg-card hover:text-fg disabled:opacity-45"
        >
          <X size={11} aria-hidden />
        </button>
      )}
    </span>
  )
}
